import { IInvoice, Invoice, InvoicePatchRequest, ApiResponse } from '$models'
import { getActiveCompanySettings, singletonQueryClient } from '$utils'
import { stockApi } from '$api'
import { InvoiceQueryConditions } from 'src/components/routes/stock/invoices/slice'
import { API_DATE_FORMAT } from '$constants'
import { InvoiceSearchResult } from '@loadedhub/loaded-stock-api-sdk'

enum QueryKeys {
  Invoices = 'invoices',
  Invoice = 'invoice',
  InitialInvoice = 'initial-invoice',
  AdjustingInvoice = 'adjusting-invoice',
  InvoiceFile = 'invoice-file',
}

/**
 * Get invoices
 * @param from - The start date for filtering invoices
 * @param to - The end date for filtering invoices
 * @param invoiceNumber - The invoice number to filter by (optional)
 * @param supplierName - The supplier name to filter by (optional)
 * @returns an array of invoices
 */
export const getInvoices = async (
  queryConditions: InvoiceQueryConditions
): Promise<ApiResponse<Invoice[]>> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.Invoices, queryConditions],
    queryFn: async ({ signal }) => {
      return stockApi.getInvoices(
        queryConditions.start?.format(API_DATE_FORMAT),
        queryConditions.end?.format(API_DATE_FORMAT),
        queryConditions.invoiceNumber,
        queryConditions.supplierName,
        queryConditions.status,
        queryConditions.exactMatch,
        queryConditions.page,
        queryConditions.pageSize,
        queryConditions.sort,
        signal
      )
    },
  })
}

/**
 * Get specific invoice
 * @param id the id of the invoice
 * @returns a specific invoices
 */
export const getInvoice = async (
  id: string,
  isAdjustingInvoice: boolean = false,
  includeDeleted: boolean = false
): Promise<IInvoice | null> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.Invoice, id, isAdjustingInvoice, includeDeleted],
    queryFn: async () => {
      return await stockApi.getInvoice(id, isAdjustingInvoice, includeDeleted)
    },
  })
}

/**
 * Get specific initial invoice
 * @param id the id of the invoice
 * @returns a specific invoices
 */
export const getInitialInvoice = async (
  id: string
): Promise<Invoice | null> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.InitialInvoice, id],
    queryFn: async () => {
      return await stockApi.getInitialInvoice(id)
    },
  })
}

/**
 * Add a new invoice
 * @param invoice the invoice to be added
 * @returns the created invoice
 */
export const addInvoice = async (invoice: IInvoice): Promise<IInvoice> => {
  return await stockApi.createInvoice(invoice as Invoice)
}

/**
 * put a new invoice
 * @param invoice the invoice to be put
 * @returns the put invoice
 */
export const updateInvoice = async (
  invoice: IInvoice
): Promise<IInvoice | null> => {
  return await stockApi.updateInvoice(invoice.id!, invoice as Invoice)
}

/**
 * remove a invoice
 * @param id the invoice id to be deleted
 */
export const removeInvoice = async (id: string): Promise<void> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return

  // purge any invoices query results from the local cache as we know this is
  // now going to be invalid
  await singletonQueryClient.invalidateQueries({
    queryKey: [QueryKeys.Invoices],
    exact: false,
    refetchType: 'none',
  })

  await stockApi.deleteInvoice(id)
}

/**
 * Restores a soft-deleted invoice by clearing its deletedAt field
 * @param invoiceId the ID of the invoice to restore
 */
export const restoreInvoice = async (
  invoiceId: string
): Promise<IInvoice | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  const invoice = await stockApi.getInvoice(invoiceId, false, true)
  if (!invoice) return null

  return await stockApi.updateInvoice(
    invoiceId,
    new Invoice({ ...invoice, deletedAt: undefined })
  )
}

export interface InvoiceFile {
  data: Blob
  contentType: string
}

export const getInvoiceFile = async (
  invoiceId: string
): Promise<InvoiceFile | null> => {
  const response = await singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.InvoiceFile, invoiceId],
    queryFn: async () => {
      return await stockApi.downloadInvoiceFile(invoiceId)
    },
  })

  return response.status === 200
    ? {
        data: response.data,
        contentType: response.headers ? response.headers['content-type'] : null,
      }
    : null
}

export const getDefaultAdjustingInvoice = async (
  supplierId: string,
  statementId: string
): Promise<IInvoice | null> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.AdjustingInvoice, supplierId, statementId],
    queryFn: async () => {
      return await stockApi.getDefaultAdjustingInvoice(supplierId, statementId)
    },
  })
}

/**
 * Update invoice third party invoice id
 * @param invoiceId - invoice id
 * @param thirdPartyInvoiceId - third party invoice id
 * @returns IInvoice
 */
export const updateThirdPartyInvoiceId = async (
  invoiceId: string,
  thirdPartyInvoiceId?: string
): Promise<IInvoice | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  return await stockApi.updateInvoiceThirdPartyInvoiceId(
    invoiceId,
    new InvoicePatchRequest({
      thirdPartyInvoiceId,
    })
  )
}

/**
 * Search invoices by term
 * @param searchTerm - The search term to match against invoice numbers and supplier names
 * @param limit - Maximum number of results to return (optional, defaults to 20)
 * @returns an array of matching invoices
 */
export const searchInvoices = async (
  searchTerm: string,
  limit?: number,
  unreceivedWithoutPo?: boolean
): Promise<InvoiceSearchResult[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.Invoices,
      'search',
      searchTerm,
      limit,
      unreceivedWithoutPo,
    ],
    queryFn: async () => {
      return stockApi.searchInvoices(searchTerm, limit, unreceivedWithoutPo)
    },
  })
}

/**
 * Deletes an invoice file
 * @param fileId the ID of the file to delete
 */
export const deleteInvoiceFile = async (fileId: string): Promise<void> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return

  await stockApi.deleteInvoiceFile(fileId)
}
